import { generatePdfUint8Array } from '../pdfUtilities';
import { Template } from '@pdfme/common';
import { mergeEstimateDetails } from './invoiceEstimatePdf';
import { PDFDocument } from 'pdf-lib';
import { getFontsForInvoice } from '../../utils/getFont';

// 1ページ目の最大項目数
const ITEM_PER_PAGE_FIRST = 14;
// 2ページ目以降の最大項目数
const ITEM_PER_PAGE_NEXT = 30;

export const invoicePdf = async (data: any) => {
  // 初回読み込みの容量を減らすため動的インポート
  const base1 = await import('../../assets/base_invoice_pdf1');
  const base2 = await import('../../assets/base_invoice_pdf2');
  const insert1 = await import('../../assets/invoice_insert_pdf1'); // 備考あり
  const insert2 = await import('../../assets/invoice_insert_pdf2'); // 備考なし
  const fonts = await getFontsForInvoice();
  const items = getEstimateItemList(data.estimates);

  const mergedPdf = await PDFDocument.create();

  // 1ページで完結する場合
  if (items.length <= ITEM_PER_PAGE_FIRST) {
    // 1ページで完結する場合は、1ページ目に基本情報と備考欄をつける
    let schemas = { ...createInvoiceInfoSchema(), ...createRemarksSchema() };
    let inputs = {
      ...createInvoiceInfoInput(data),
      ...createRemarksInput(data)
    };

    const page: any = await generateInvoicePage(
      items,
      fonts,
      0,
      items.length,
      212.3,
      schemas,
      inputs,
      base1.basePdf
    );
    await mergePdfPage(page, mergedPdf);

    // 見積書明細ページを追加
    if (data.output_details) {
      await mergeEstimateDetails(data, fonts, mergedPdf);
    }

    const exportPdf = await mergedPdf.save();

    return new Blob([exportPdf.buffer], { type: 'application/pdf' });
  }

  // 複数ページ必要な場合

  // 項目数から、必要なページ数を計算
  const remainingItems = items.slice(ITEM_PER_PAGE_FIRST); // baseページ以外の見積データを取得
  const totalPages = Math.ceil(remainingItems.length / ITEM_PER_PAGE_NEXT) + 1; // insertページのページ数の取得

  // ページの最初の項目と最後の項目のインデックス
  let startIdx = 0;
  let endIdx = ITEM_PER_PAGE_FIRST;

  for (let pageIndex = 1; pageIndex <= totalPages; pageIndex++) {
    // ページを生成するためのプロパティ
    let schemas, inputs, offsetY, basePdf;

    // 1ページ目には商品発注書基本情報をつける
    if (pageIndex === 1) {
      schemas = createInvoiceInfoSchema();
      inputs = createInvoiceInfoInput(data);
      offsetY = 212.3;
      basePdf = base2.basePdf;
    } else if (pageIndex === totalPages) {
      // 最終ページには備考・小計欄をつける
      schemas = createRemarksSchema();
      inputs = createRemarksInput(data);
      offsetY = 31.55;
      basePdf = insert1.basePdf;
    } else {
      schemas = {};
      inputs = {};
      offsetY = 31.55;
      basePdf = insert2.basePdf;
    }

    // ページを生成
    const page = await generateInvoicePage(
      items,
      fonts,
      startIdx,
      endIdx,
      offsetY,
      schemas,
      inputs,
      basePdf
    );

    await mergePdfPage(page, mergedPdf);

    // ページの最初の項目と最後の項目のインデックスを更新
    startIdx = endIdx;
    endIdx = Math.min(items.length, endIdx + ITEM_PER_PAGE_NEXT);
  }

  // 見積書明細ページを追加
  if (data.output_details) {
    await mergeEstimateDetails(data, fonts, mergedPdf);
  }

  const exportPdf = await mergedPdf.save();

  return new Blob([exportPdf.buffer], { type: 'application/pdf' });
};

const getEstimateItemList = (estimates: any) => {
  let items: any = [];
  for (let i = 0; i < estimates.length; i++) {
    if (Array.isArray(estimates[i].items) && estimates[i].items.length > 0) {
      items.push({
        ...estimates[i].items[0],
        quantity: 1,
        code: estimates[i].code ? estimates[i].code : '',
        construction_name: estimates[i].construction_name
          ? estimates[i].construction_name.replace(/\r\n|\r|\n/g, ' ')
          : '',
        selling_unit_price: estimates[i].billing_total
          ? estimates[i].billing_total
          : 0
      });
    } else {
      items.push({
        quantity: 1,
        code: estimates[i].code ? estimates[i].code : '',
        construction_name: estimates[i].construction_name
          ? estimates[i].construction_name.replace(/\r\n|\r|\n/g, ' ')
          : '',
        selling_unit_price: estimates[i].billing_total
          ? estimates[i].billing_total
          : 0
      });
    }
  }
  return items;
};

const generateInvoicePage = async (
  items: any,
  font: any,
  startIdx: number,
  endIdx: number,
  offsetY: number,
  schemas: any,
  inputs: any,
  basePdf: any
) => {
  for (let idx = startIdx; idx < endIdx; idx++) {
    const item = items[idx];
    let idxInPage = idx - startIdx;
    addItemRow(schemas, inputs, item, idx, offsetY + idxInPage * 11.3);
  }

  const template: Template = {
    basePdf,
    schemas: [schemas]
  };

  return await generatePdfUint8Array(template, [inputs], font);
};

const mergePdfPage = async (pageX: any, mergedPdf: any) => {
  const pdfDoc = await PDFDocument.load(pageX);

  const copiedPage = await mergedPdf.copyPages(pdfDoc, pdfDoc.getPageIndices());
  copiedPage.forEach((page: any) => mergedPdf.addPage(page));

  return pdfDoc;
};

/**
 * 各項目を表すスキーマと入力データを、
 * PDF出力のためのスキーマオブジェクトと入力データオブジェクトに追加する
 * @param schemas PDF出力のためのスキーマオブジェクト
 * @param inputs PDF出力のための入力データオブジェクト
 * @param item 項目データ
 * @param indexInPage ページ内でのインデックス
 * @param yPos y方向開始位置
 */
const addItemRow = (
  schemas: any,
  inputs: any,
  item: any,
  indexInPage: number,
  yPos: number
) => {
  // 見積書の単位が干渉しないように式で固定する
  // 自由選択が実装できるまでの暫定対応
  const invoiceUnit = '式';

  const commonSchema = {
    type: 'text',
    height: 11,
    verticalAlignment: 'middle'
  };

  schemas[`estimate_construction_name${indexInPage}`] = {
    // 見積書番号- 大項目名
    position: {
      x: 32,
      y: yPos
    },
    width: 139,
    alignment: 'left',
    dynamicFontSize: {
      max: 10,
      min: 6
    },
    ...commonSchema
  };
  schemas[`estimate_item_unit_price${indexInPage}`] = {
    // 単価
    position: {
      x: 169.99,
      y: yPos
    },
    width: 32.88,
    alignment: 'right',
    fontSize: 11,
    ...commonSchema
  };
  schemas[`estimate_item_quantity${indexInPage}`] = {
    // 数量 quantity + unitName
    position: {
      x: 206,
      y: yPos
    },
    width: 20.98,
    alignment: 'center',
    fontSize: 11,
    ...commonSchema
  };
  schemas[`estimate_item_total_price${indexInPage}`] = {
    // 金額単価 * quantity
    position: {
      x: 227.01,
      y: yPos
    },
    width: 32.09,
    alignment: 'right',
    fontSize: 11,
    ...commonSchema
  };

  inputs[`estimate_construction_name${indexInPage}`] = `${
    item.code === '見積書なし' ? '' : item.code + ' - '
  }${item.construction_name}`;
  inputs[`estimate_item_unit_price${indexInPage}`] = `￥${Number(
    item.selling_unit_price
  ).toLocaleString('ja-JP')}`;
  inputs[`estimate_item_quantity${indexInPage}`] = `${Number(
    item.quantity
  ).toLocaleString('ja-JP')}${invoiceUnit}`;
  inputs[`estimate_item_total_price${indexInPage}`] = `￥${(
    item.selling_unit_price * item.quantity
  ).toLocaleString('ja-JP')}`;
};

/**
 * 請求書基本情報のスキーマを作成する
 * @returns 請求書基本情報のスキーマ
 */
const createInvoiceInfoSchema = () => {
  return {
    published_date: {
      // 日付
      type: 'text',
      position: {
        x: 219.92,
        y: 46.2,
      },
      width: 43,
      height: 7,
      alignment: 'left',
      fontSize: 12,
      characterSpacing: 0,
      lineHeight: 1
    },
    construct_code: { 
       // 工事番号
        type: 'text',
        position: {
          x: 220,
          y: 56.6
        },
        width: 35,
        height: 7,
        alignment: "left",
        fontSize: 12,
        characterSpacing: 0,
        lineHeight: 1
      },
    invoice_code: { 
      // 請求書番号
      type: 'text',
      position: {
        x: 220,
        y: 67
      },
      width: 35,
      height: 7,
      alignment: 'left',
      fontSize: 12,
      characterSpacing: 0,
      lineHeight: 1
    },
    contractor_name: {
      // 請求先企業名
      type: 'text',
      position: {
        x: 29,
        y: 55.01
      },
      width: 140,
      height: 10.5,
      alignment: 'left',
      fontSize: 16,
      characterSpacing: 0,
      lineHeight: 1
    },
    company_code: {
      // 登録番号
      type: 'text',
      position: {
        x: 175,
        y: 90
      },
      width: 79.98,
      height: 7,
      alignment: 'left',
      fontSize: 10,
      characterSpacing: 0,
      lineHeight: 1
    },
    construction_license: {
      // 建設業許可番号
      type: 'text',
      position: {
        x: 175,
        y: 94
      },
      width: 79.98,
      height: 7,
      alignment: 'left',
      fontSize: 10,
      characterSpacing: 0,
      lineHeight: 1
    },
    payment_date: {
      // 振込期日
      type: 'text',
      position: {
        x: 99.01,
        y: 163.99
      },
      width: 163.06,
      height: 10.96,
      alignment: 'center',
      fontSize: 12,
      characterSpacing: 0,
      lineHeight: 2.5
    },
    sub_total: {
      // 小計（今回請求金額）
      type: 'text',
      position: {
        x: 29,
        y: 102.16
      },
      width: 41.87,
      height: 10.7,
      alignment: 'center',
      fontSize: 12,
      characterSpacing: 0,
      lineHeight: 2.5
    },
    tax_of_billing_total: {
      // 今回請求金額にかかる消費税
      type: 'text',
      position: {
        x: 70.99,
        y: 102
      },
      width: 42.94,
      height: 10.7,
      alignment: 'center',
      fontSize: 12,
      characterSpacing: 0,
      lineHeight: 2.5
    },
    billing_total_including_tax: {
      // 今回請求の合計金額
      fontName: 'minchoBold',
      type: 'text',
      position: {
        x: 114.01,
        y: 100
      },
      width: 55.9,
      height: 10.7,
      alignment: 'center',
      fontSize: 16,
      characterSpacing: 0,
      lineHeight: 2.5
    },
    contract_total: {
      // 御契約金額
      type: 'text',
      position: {
        x: 99.06,
        y: 116.53
      },
      width: 67.8,
      height: 10.69,
      alignment: 'right',
      fontSize: 12,
      characterSpacing: 0,
      lineHeight: 2.5
    },
    already_paid: {
      // 御支払済金額
      type: 'text',
      position: {
        x: 99.01,
        y: 127.8
      },
      width: 68.07,
      height: 10.69,
      alignment: 'right',
      fontSize: 12,
      characterSpacing: 0,
      lineHeight: 2.5
    },
    billing_total: {
      // 今回請求金額
      type: 'text',
      position: {
        x: 99.01,
        y: 138.93
      },
      width: 68.07,
      height: 10.69,
      alignment: 'right',
      fontSize: 12,
      characterSpacing: 0,
      lineHeight: 2.5
    },
    balance_total: {
      // 残金
      type: 'text',
      position: {
        x: 99.01,
        y: 149.96
      },
      width: 68.07,
      height: 10.69,
      alignment: 'right',
      fontSize: 12,
      characterSpacing: 0,
      lineHeight: 2.5
    },
    payee1_1: {
      // 振込先
      type: 'text',
      position: {
        x: 100,
        y: 180.11
      },
      width: 162.79,
      height: 12.02,
      alignment: 'center',
      fontSize: 11,
      characterSpacing: 0,
      lineHeight: 2.5
    },
    payee2_1: {
      // 振込先
      type: 'text',
      position: {
        x: 100,
        y: 178.11
      },
      width: 162.79,
      height: 12.02,
      alignment: 'center',
      fontSize: 11,
      characterSpacing: 0,
      lineHeight: 2.5
    },
    payee2_2: {
      // 振込先
      type: 'text',
      position: {
        x: 100,
        y: 183.11
      },
      width: 162.79,
      height: 12.02,
      alignment: 'center',
      fontSize: 11,
      characterSpacing: 0,
      lineHeight: 2.5
    },
    payee3_1: {
      // 振込先
      type: 'text',
      position: {
        x: 100,
        y: 175.11
      },
      width: 162.79,
      height: 12.02,
      alignment: 'center',
      fontSize: 11,
      characterSpacing: 0,
      lineHeight: 2.5
    },
    payee3_2: {
      // 振込先
      type: 'text',
      position: {
        x: 100,
        y: 180.11
      },
      width: 162.79,
      height: 12.02,
      alignment: 'center',
      fontSize: 11,
      characterSpacing: 0,
      lineHeight: 2.5
    },
    payee3_3: {
      // 振込先
      type: 'text',
      position: {
        x: 100,
        y: 185.11
      },
      width: 162.79,
      height: 12.02,
      alignment: 'center',
      fontSize: 11,
      characterSpacing: 0,
      lineHeight: 2.5
    },
    company_name: {
      // 自社名
      type: 'text',
      position: {
        x: 175,
        y: 79
      },
      width: 85.01,
      height: 7,
      alignment: 'left',
      fontSize: 14,
      characterSpacing: 0,
      lineHeight: 1
    },
    company_postal_code: {
      // 郵便番号
      type: 'text',
      position: {
        x: 175,
        y: 103.9
      },
      width: 35,
      height: 7,
      alignment: 'left',
      fontSize: 12,
      characterSpacing: 0,
      lineHeight: 1
    },
    company_address: {
      // 住所
      type: 'text',
      position: {
        x: 175,
        y: 111.06
      },
      width: 85,
      height: 5.66,
      alignment: 'left',
      fontSize: 12,
      characterSpacing: 0,
      lineHeight: 1
    },
    sales_user_name: {
      // 営業担当
      type: 'text',
      position: {
        x: 175,
        y: 125.25
      },
      width: 57,
      height: 7,
      alignment: 'left',
      fontSize: 12,
      characterSpacing: 0,
      lineHeight: 1
    },
    company_phone_number: {
      // 電話番号
      type: 'text',
      position: {
        x: 175,
        y: 140.25
      },
      width: 49.55,
      height: 7,
      alignment: 'left',
      fontSize: 12,
      characterSpacing: 0,
      lineHeight: 1
    },
    company_fax_number: {
      // FAX番号
      type: 'text',
      position: {
        x: 175,
        y: 145.25
      },
      width: 49.55,
      height: 7,
      alignment: 'left',
      fontSize: 12,
      characterSpacing: 0,
      lineHeight: 1
    },
    representative: {
      // 代表者
      type: 'text',
      position: {
        x: 175,
        y: 136.24
      },
      width: 48.75,
      height: 6.99,
      alignment: 'left',
      fontSize: 12,
      characterSpacing: 0,
      lineHeight: 1
    },
    tax_rate_label: {
      // 消費税率
      type: 'text',
      position: {
        x: 92.68,
        y: 94.02
      },
      width: 13.29,
      height: 6.98,
      alignment: 'center',
      fontSize: 12,
      characterSpacing: 0,
      lineHeight: 1
    },
    contract_tax_rate: {
      // 小計の税率
      type: 'text',
      position: {
        x: 50.68,
        y: 94.02
      },
      width: 17.29,
      height: 6.98,
      alignment: 'center',
      fontSize: 12,
      characterSpacing: 0,
      lineHeight: 1
    },
    companyStamp: {
      // 会社印
      type: 'image',
      position: {
        x: 232.03,
        y: 125.62
      },
      width: 26.53,
      height: 26.53,
      alignment: 'left',
      fontSize: 13,
      characterSpacing: 0,
      lineHeight: 1
    },
    free_field: {
      type: 'text',
      position: {
        x: 145,
        y: 150.11
      },
      width: 162.79,
      height: 12.02,
      alignment: 'center',
      fontSize: 11,
      characterSpacing: 0,
      lineHeight: 2.5
    },
    free_field_top: {
      type: 'text',
      position: {
        x: 173,
        y: 147.11
      },
      width: 162.79,
      height: 12.02,
      fontSize: 11,
      characterSpacing: 0,
      lineHeight: 2.5
    },
    free_field_seconds: {
      type: 'text',
      position: {
        x: 173,
        y: 152.11
      },
      width: 162.79,
      height: 12.02,
      fontSize: 11,
      characterSpacing: 0,
      lineHeight: 2.5
    }
  };
};

/**
 * 請求書基本情報のPDF出力のためのデータを作成する
 * @param invoice 請求書データ
 * @returns 請求書基本情報のPDF出力のためのデータ
 */
const createInvoiceInfoInput = (invoice: any) => {
  let freeField = '',
    freeFieldTop = '',
    freeFieldSecond = '';
  if (invoice.free_field.length > 27) {
    freeFieldTop = invoice.free_field.substr(0, 27);
    freeFieldSecond = invoice.free_field.substr(27, 23);
  } else {
    freeField = invoice.free_field;
  }

  return {
    published_date: `${invoice.published_date_str}`, // 顧客名 〜御中
    construct_code: `${invoice.construct_code}`, // 工事番号
    invoice_code: `${invoice.code}`, // 請求書番号
    contractor_name: `${invoice.client_name} ${invoice.honorific_title}`, // 顧客名 〜御中
    tax_rate_label: `${String(invoice.tax_rate * 100)}%`, // 消費税 ％
    contract_tax_rate: `${String(invoice.tax_rate * 100)}%対象`,
    tax: `¥${Number(invoice.tax).toLocaleString('ja-JP')}`, // 消費税
    tax_of_billing_total: `¥${Number(
      invoice.tax_of_billing_total
    ).toLocaleString('ja-JP')}`, // 消費税
    billing_total_including_tax: `¥${Number(
      invoice.billing_total_including_tax
    ).toLocaleString('ja-JP')}`, // 今回請求の合計金額
    contract_total: `¥${Number(invoice.contract_total).toLocaleString(
      'ja-JP'
    )}`, // 御契約金額
    already_paid: `¥${Number(invoice.already_paid).toLocaleString('ja-JP')}`, // 御支払済
    billing_total: `¥${Number(invoice.billing_total).toLocaleString('ja-JP')}`, // 今回請求
    sub_total: `¥${Number(invoice.billing_total).toLocaleString('ja-JP')}`, // 小計(今回請求金額)
    balance_total: `¥${Number(invoice.balance_total).toLocaleString('ja-JP')}`, // 残金
    payment_date: invoice.payment_date_str, // 振込期日
    company_name: invoice.company_name, // 会社名
    company_code:
      invoice.company_code !== '' ? `登録番号: ${invoice.company_code}` : '', // 会社番号 機能実装後に追加
    construction_license:
      invoice.construction_license !== ''
        ? `建設業許可: ${invoice.construction_license}`
        : '', // 建設業許可番号
    company_postal_code: `〒${invoice.company_postal_code.slice(
      0,
      3
    )} - ${invoice.company_postal_code.slice(-4)}`, // 郵便番号
    company_address: invoice.company_address,
    company_phone_number: `電話 ${invoice.company_phone_number}`, // 電話番号
    company_fax_number:
      invoice.company_fax_number != ''
        ? `FAX ${invoice.company_fax_number}`
        : '', // FAX番号
    representative: '', // 代表者名 必要なデータが存在しないため、一時保留
    sales_user_name: invoice.output_pdf
      ? invoice.sales_user_name
        ? `営業担当: ${invoice.sales_user_name}`
        : ''
      : '',
    companyStamp:
      invoice.company_stamp && invoice.company_stamp !== ''
        ? `data:${invoice.company_stamp_format};base64,${invoice.company_stamp}`
        : '', // 印鑑
    free_field: freeField,
    free_field_top: freeFieldTop,
    free_field_seconds: freeFieldSecond,
    ...createPayeeInput(invoice) // 振込先
  };
};

/**
 * 振込先文字列を加工して、振込先の出力データを作成する
 * @param invoice 請求書データ
 * @returns 振込先の出力データ
 */
const createPayeeInput = (invoice: any) => {
  // 振込先
  //invoiceにあるpayeeの文字列を配列に格納
  //invoiceにあるpayeeには空文字が含まれている場合があるので、空文字を取り除く。
  const payeeDataArray: string[] = invoice.payee
    .split('\n')
    .filter((item: string) => item.trim() !== '');

  if (payeeDataArray.length === 3) {
    return {
      payee3_1: payeeDataArray[0],
      payee3_2: payeeDataArray[1],
      payee3_3: payeeDataArray[2]
    };
  } else if (payeeDataArray.length === 2) {
    return {
      payee2_1: payeeDataArray[0],
      payee2_2: payeeDataArray[1]
    };
  } else {
    return {
      payee1_1: payeeDataArray[0] ? payeeDataArray[0] : ''
    };
  }
};

/**
 * 備考欄のスキーマを作成する
 * @returns 備考欄のスキーマ
 */
const createRemarksSchema = () => {
  return {
    remarks: {
      // 備考
      type: 'text',
      position: {
        x: 73,
        y: 375
      },
      width: 188,
      height: 17.05,
      alignment: 'left',
      fontSize: 12
    }
  };
};

/**
 * 備考欄のPDF出力のためのデータを作成する
 * @param invoice 請求書データ
 * @returns 備考欄のPDF出力のためのデータ
 */
const createRemarksInput = (invoice: any) => {
  return {
    remarks: invoice.remarks // 備考
  };
};
